import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface LocationData {
  id: string
  name: string
}

interface ProductData {
  id: string | number
  name: string
}

interface DoctorData {
  id: number
  name: string
}

interface PaymentData {
  idPlan: number
  idCarrier: number
  healthCarrier: string
  healthPlan: string
  isPrivate: boolean
}

interface PatientData {
  birthDate: string
  gender: string
  id: string
}

interface CartRecoveryState {
  location?: LocationData
  product?: ProductData
  doctor?: DoctorData
  payment?: PaymentData
  patient?: PatientData
  flowType?: string
  alreadyRecovered?: boolean
  savedAt?: string

  showModal: boolean
  hasCartRecovery: boolean
  isCheckingCartRecovery: boolean

  setLocation: (data?: LocationData) => void
  setProduct: (data?: ProductData) => void
  setDoctor: (data?: DoctorData) => void
  setPayment: (data?: PaymentData) => void
  setPatient: (data?: PatientData) => void
  setFlowType: (flowType?: string) => void

  setShowModal: (value: boolean) => void
  setHasCartRecovery: (value: boolean) => void
  setAlreadyRecovered: (value: boolean) => void
  setIsCheckingCartRecovery: (value: boolean) => void
  updateSavedAt: () => void

  reset: () => void
}

const initialState = {
  location: undefined,
  product: undefined,
  doctor: undefined,
  payment: undefined,
  patient: undefined,
  showModal: false,
  hasCartRecovery: false,
  flowType: undefined,
  alreadyRecovered: false,
  isCheckingCartRecovery: true,
  savedAt: undefined
}

export const useCartRecoveryStore = create<CartRecoveryState>()(
  devtools(
    persist(
      (set) => ({
        showModal: false,
        hasCartRecovery: false,
        alreadyRecovered: false,
        isCheckingCartRecovery: true,
        savedAt: undefined,

        setLocation: (data) => set({ location: data }),
        setProduct: (data) => set({ product: data }),
        setDoctor: (data) => set({ doctor: data }),
        setPayment: (data) => set({ payment: data }),
        setPatient: (data) => set({ patient: data }),

        setAlreadyRecovered: (value: boolean) =>
          set({ alreadyRecovered: value }),
        setShowModal: (value) => set({ showModal: value }),
        setHasCartRecovery: (value) => set({ hasCartRecovery: value }),
        setIsCheckingCartRecovery: (value: boolean) =>
          set({ isCheckingCartRecovery: value }),
        setFlowType: (flowType) => set({ flowType }),
        updateSavedAt: () => set({ savedAt: new Date().toISOString() }),

        reset: () => set(initialState)
      }),
      {
        name: 'cart-recovery-store',
        partialize: (state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { isCheckingCartRecovery, showModal, ...persistedState } = state
          return persistedState
        }
      }
    )
  )
)
